import React, {
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box, GlobalStyles, Typography } from '@mui/material';
import { MytaverseLogger } from '@helpers/logger';
import { entryPromptTimeout } from '@constants/entryPrompt';
import { ICinemaPalette } from '../palette';
import {
  IMiniGameHandle,
  MiniGameEvent,
  MiniGameFailure,
  MiniGameId,
} from './types';
import { loadMiniGame, miniGameOf, pickableGames } from './catalog';
import { createKeyGuard } from './keyGuard';
import {
  isTouchPrimary,
  prefersReducedMotion,
  readMuted,
  toMiniGamePalette,
  writeMuted,
} from './gameOptions';
import { formatClock, isActiveSince, remainingShare } from './readyClock';
import { getOverlayStyles } from './overlayStyles';
import { miniGameGlobalStyles } from './styleTokens';
import FailureCard from './FailureCard';
import ReadyBanner from './ReadyBanner';
import OverlayHeader, { IMiniGameStatus } from './OverlayHeader';
import { MiniGameCloseReason } from './useMiniGames';
import {
  MINI_GAME_CLOCK_TICK_MS,
  MINI_GAME_SEAT_HELD,
  MINI_GAME_SEAT_HELD_PLAYING,
  MINI_GAME_WARMING_UP,
} from './constants';

const FOREIGN_UI =
  'input, textarea, select, [contenteditable=""], [contenteditable="true"], [role="dialog"], [role="alertdialog"], .MuiModal-root';

const HOST_ACTIVITY_EVENTS = ['pointerdown', 'pointermove', 'wheel'] as const;

type OverlayPhase = 'loading' | 'playing' | 'failed';

interface IMiniGameOverlayProps {
  gameId: MiniGameId;
  palette: ICinemaPalette;
  logoUrl: string | null;
  avatarUrl: string | null;
  status: IMiniGameStatus;
  ready: boolean;
  deadlineAt: number | null;
  activityRef: MutableRefObject<number>;
  animateIn: boolean;
  onEvent: (event: MiniGameEvent) => void;
  onEnter: () => void;
  onClose: (reason: MiniGameCloseReason) => void;
  onSwitch: (id: MiniGameId) => void;
}

const callGame = (
  handle: IMiniGameHandle | null,
  action: (game: IMiniGameHandle) => void,
) => {
  if (!handle) {
    return;
  }

  try {
    action(handle);
  } catch (error) {
    MytaverseLogger.warn('Mini game call failed', {
      error: error instanceof Error ? error.name : typeof error,
    });
  }
};

const pageHidden = () => document.visibilityState === 'hidden';

const MiniGameOverlay = ({
  gameId,
  palette,
  logoUrl,
  avatarUrl,
  status,
  ready,
  deadlineAt,
  activityRef,
  animateIn,
  onEvent,
  onEnter,
  onClose,
  onSwitch,
}: IMiniGameOverlayProps) => {
  const game = miniGameOf(gameId);
  const classes = useMemo(() => getOverlayStyles(palette), [palette]);
  const switchable = useMemo(
    () => (game.kind === 'game' ? pickableGames(isTouchPrimary()) : []),
    [game],
  );
  const rootRef = useRef<HTMLDivElement | null>(null);
  const hostRef = useRef<HTMLDivElement | null>(null);
  const handleRef = useRef<IMiniGameHandle | null>(null);
  const readyPausedRef = useRef(ready);
  const mutedRef = useRef(readMuted());
  const callbacksRef = useRef({ onEvent, onClose });
  const [phase, setPhase] = useState<OverlayPhase>('loading');
  const [failure, setFailure] = useState<MiniGameFailure | null>(null);
  const [muted, setMuted] = useState(mutedRef.current);
  const [bannerOpen, setBannerOpen] = useState(ready);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    callbacksRef.current = { onEvent, onClose };
  });

  const markActivity = useCallback(() => {
    activityRef.current = Date.now();
  }, [activityRef]);

  useEffect(() => {
    const guard = createKeyGuard({
      target: window,
      onKeyDown: (event) => {
        markActivity();
        callGame(handleRef.current, (handle) => handle.keyDown(event));
      },
      onKeyUp: (event) => {
        markActivity();
        callGame(handleRef.current, (handle) => handle.keyUp(event));
      },
      onEscape: () => callbacksRef.current.onClose('escape'),
      onRelease: () =>
        callGame(handleRef.current, (handle) => handle.releaseKeys()),
      passThrough: (target) =>
        target instanceof Element &&
        !rootRef.current?.contains(target) &&
        target.closest(FOREIGN_UI) !== null,
    });

    return () => guard.dispose();
  }, [markActivity]);

  useEffect(() => {
    const host = hostRef.current;

    if (!host) {
      return;
    }

    let active = true;
    host.focus({ preventScroll: true });

    const emit = (event: MiniGameEvent) => {
      if (!active) {
        return;
      }

      if (event.type === 'loaded') {
        setPhase((current) => (current === 'failed' ? current : 'playing'));
      } else if (event.type === 'failed') {
        setPhase('failed');
        setFailure(event.reason);
      }

      callbacksRef.current.onEvent(event);
    };

    loadMiniGame(gameId).then(
      (mount) => {
        if (!active) {
          return;
        }

        try {
          const handle = mount(host, {
            palette: toMiniGamePalette(palette),
            logoUrl,
            avatarUrl,
            reducedMotion: prefersReducedMotion(),
            touch: isTouchPrimary(),
            muted: mutedRef.current,
            onEvent: emit,
          });
          handleRef.current = handle;

          if (readyPausedRef.current || pageHidden()) {
            callGame(handle, (game) => game.pause());
          }
        } catch (error) {
          emit({
            type: 'failed',
            reason: 'runtime',
            message: error instanceof Error ? error.name : 'mount failed',
          });
        }
      },
      () =>
        emit({
          type: 'failed',
          reason: 'assets',
          message: 'the game module could not be loaded',
        }),
    );

    return () => {
      active = false;
      const handle = handleRef.current;
      handleRef.current = null;
      callGame(handle, (game) => game.releaseKeys());
      callGame(handle, (game) => game.dispose());
    };
  }, []);

  useEffect(() => {
    const onVisibility = () => {
      if (pageHidden()) {
        callGame(handleRef.current, (handle) => handle.pause());
      } else if (!readyPausedRef.current) {
        callGame(handleRef.current, (handle) => handle.resume());
      }
    };

    document.addEventListener('visibilitychange', onVisibility);

    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  useEffect(() => {
    const host = hostRef.current;

    if (!host) {
      return;
    }

    const preventMenu = (event: Event) => event.preventDefault();
    HOST_ACTIVITY_EVENTS.forEach((type) =>
      host.addEventListener(type, markActivity, {
        capture: true,
        passive: true,
      }),
    );
    host.addEventListener('contextmenu', preventMenu);

    return () => {
      HOST_ACTIVITY_EVENTS.forEach((type) =>
        host.removeEventListener(type, markActivity, { capture: true }),
      );
      host.removeEventListener('contextmenu', preventMenu);
    };
  }, [markActivity]);

  useEffect(() => {
    if (readyPausedRef.current === ready) {
      return;
    }

    readyPausedRef.current = ready;
    setBannerOpen(ready);

    if (ready) {
      callGame(handleRef.current, (handle) => handle.pause());
    } else if (!pageHidden()) {
      callGame(handleRef.current, (handle) => handle.resume());
    }
  }, [ready]);

  useEffect(() => {
    if (!ready || deadlineAt === null) {
      return;
    }

    setNow(Date.now());
    const interval = setInterval(
      () => setNow(Date.now()),
      MINI_GAME_CLOCK_TICK_MS,
    );

    return () => clearInterval(interval);
  }, [ready, deadlineAt]);

  const keepPlaying = useCallback(() => {
    readyPausedRef.current = false;
    markActivity();
    setBannerOpen(false);

    if (!pageHidden()) {
      callGame(handleRef.current, (handle) => handle.resume());
    }

    hostRef.current?.focus({ preventScroll: true });
  }, [markActivity]);

  const toggleMuted = useCallback(() => {
    const next = !mutedRef.current;
    mutedRef.current = next;
    writeMuted(next);
    setMuted(next);
    callGame(handleRef.current, (handle) => handle.setMuted(next));
  }, []);

  const clock =
    ready && deadlineAt !== null
      ? isActiveSince(activityRef.current, now)
        ? { label: MINI_GAME_SEAT_HELD_PLAYING, time: null, share: null }
        : {
            label: MINI_GAME_SEAT_HELD,
            time: formatClock(deadlineAt - now),
            share: remainingShare(deadlineAt, now, entryPromptTimeout),
          }
      : null;
  const clockLine = clock ? (
    <>
      {clock.label}
      {clock.time ? <b> {clock.time}</b> : null}
    </>
  ) : null;
  const failed = phase === 'failed';

  return (
    <Box ref={rootRef} sx={classes.overlay} data-testid="myta-minigame-overlay">
      <GlobalStyles styles={miniGameGlobalStyles} />
      <Box sx={[classes.backdrop, !animateIn && classes.still]} />
      <Box
        sx={[classes.panel, !animateIn && classes.still]}
        role="dialog"
        aria-label={game.title}
      >
        <OverlayHeader
          classes={classes}
          palette={palette}
          game={game}
          switchable={switchable}
          status={status}
          ready={ready}
          bannerOpen={bannerOpen}
          clockLine={clockLine}
          muted={muted}
          onEnter={onEnter}
          onToggleMuted={toggleMuted}
          onClose={() => onClose('user')}
          onSwitch={onSwitch}
        />
        <Box sx={classes.viewport}>
          <Box
            ref={hostRef}
            tabIndex={-1}
            sx={classes.host}
            data-testid="myta-minigame-host"
          />
          {phase === 'loading' && !(ready && bannerOpen) ? (
            <Box sx={classes.warming} data-testid="myta-minigame-warming">
              <Box sx={classes.warmingDots}>
                <span />
                <span />
                <span />
              </Box>
              <Typography component="p" sx={classes.warmingTitle}>
                {MINI_GAME_WARMING_UP}
              </Typography>
              <Typography component="span" sx={classes.warmingSub}>
                {game.title}
              </Typography>
            </Box>
          ) : null}
          {failed && !(ready && bannerOpen) ? (
            <FailureCard
              classes={classes}
              failure={failure}
              onBack={() => onClose('user')}
            />
          ) : null}
          {ready && bannerOpen ? (
            <ReadyBanner
              classes={classes}
              clockLine={clockLine}
              fuseShare={clock ? clock.share : null}
              failed={failed}
              onEnter={onEnter}
              onKeepPlaying={keepPlaying}
              onBack={() => onClose('user')}
            />
          ) : null}
        </Box>
      </Box>
    </Box>
  );
};

export default MiniGameOverlay;
